import { faSave } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { faKey } from '@fortawesome/pro-light-svg-icons';
import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import stringify from 'json-stable-stringify';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tss } from 'tss-react';

import * as State from 'components/desktop/User/User/State';
import * as ApiKey from 'types/User/Admin/UserApiKey/UserApiKey';

import { Handlers } from '../User/useState';

interface OverlayContentProps {
	state: State.Type;
	handlers: Handlers;
}

const OverlayContent = (props: OverlayContentProps) => {
	const [apiKey, setApiKey] = useState<ApiKey.Type>(
		State.getCurrentApiKey(props.state) ?? ApiKey.create()
	);
	const { t } = useTranslation();
	const { classes, cx } = useStyles();

	useEffect(() => {
		setApiKey(State.getCurrentApiKey(props.state) ?? ApiKey.create());
	}, [props.state]);

	const isButtonDisabled = useMemo(() => {
		if (apiKey.id === 'new') return true;
		return stringify(apiKey) === stringify(State.getCurrentApiKey(props.state));
	}, [apiKey]);

	return (
		<div className={classes.container}>
			<div className={classes.title}>{t('_general:GENERATE_API_KEY')}</div>
			<div>
				<label htmlFor="api-key-name" className={cx('form-label', classes.label)}>
					{t('_general:NAME')}
				</label>
				<input
					type="text"
					className="form-control"
					error-key="DisplayName"
					placeholder="..."
					id="api-key-name"
					value={apiKey.displayName}
					onChange={(e) => {
						setApiKey({ ...apiKey, displayName: e.target.value });
					}}
				/>
			</div>
			<div>
				<label htmlFor="api-key-date" className={cx('form-label', classes.label)}>
					{t('user:EXPIRATION_DATE')}
				</label>
				<input
					type="date"
					min={new Date().toISOString().split('T')[0]}
					className="form-control"
					id="api-key-date"
					value={ApiKey.getExpirationTimeString(apiKey)}
					onChange={(e) => {
						setApiKey({ ...apiKey, expirationTime: new Date(e.target.value) });
					}}
				/>
			</div>
			<button
				className="btn btn-primary"
				onClick={async () => {
					await props.handlers.generateApiKey(apiKey);
				}}
				disabled={apiKey.id !== 'new' || apiKey.displayName === ''}
			>
				<FontAwesomeIcon icon={faKey} className={classes.icon} />
				{t('_general:GENERATE')}
			</button>
			{apiKey.secretKey && (
				<div className={cx('text-primary', classes.secretKeyMessage)}>
					{t('user:SECRET_KEY_MESSAGE')}
				</div>
			)}
			<div
				className={['input-group', apiKey.secretKey === undefined ? 'd-none' : ''].join(
					' '
				)}
			>
				<input
					type="text"
					className="form-control"
					placeholder={apiKey.secretKey ?? '...'}
					disabled
				/>
				<button
					className="btn btn-primary"
					onClick={async () => {
						navigator.clipboard.writeText(apiKey.secretKey ?? '');
					}}
					disabled={apiKey.secretKey === undefined}
				>
					<FontAwesomeIcon icon={faCopy} className={classes.icon} />
					{t('_general:COPY')}
				</button>
			</div>
			<div className={classes.buttons}>
				<button className="btn btn-outline-primary" onClick={props.handlers.clickCancel}>
					<FontAwesomeIcon icon={faXmark} className={classes.icon} />
					{t('_general:CANCEL')}
				</button>
				<button
					className="btn btn-primary"
					onClick={async () => {
						await props.handlers.saveApiKey(apiKey);
					}}
					disabled={isButtonDisabled}
				>
					<FontAwesomeIcon icon={faSave} className={classes.icon} />
					{t('_general:BTN_SAVE')}
				</button>
			</div>
		</div>
	);
};

export default OverlayContent;

const useStyles = tss.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: '550px',
		gap: '10px',
	},
	button: {
		cursor: 'pointer',
		minWidth: '100px',
		display: 'flex',
		justifyContent: 'center',
	},
	title: {
		fontWeight: 'bold',
	},
	label: {
		marginBottom: '0px',
	},
	buttons: {
		display: 'flex',
		gap: '10px',
		justifyContent: 'flex-end',
	},
	icon: {
		marginRight: '5px',
	},
	secretKeyMessage: {
		marginTop: '0px',
		marginBottom: '-5px',
		fontSize: '0.8em',
	},
});
